import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TrainingGuide, TrainingGuidesService } from '../training-guides.service';

const TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  text: { label: 'Text', icon: '📄' },
  pdf: { label: 'PDF', icon: '📕' },
  pptx: { label: 'Presentation', icon: '🖥️' },
  video: { label: 'Video', icon: '🎬' },
};

@Component({
  selector: 'app-training-hub',
  templateUrl: './training-hub.component.html',
  styleUrls: ['./training-hub.component.scss'],
  standalone: false,
})
export class TrainingHubComponent implements OnInit {
  guides: TrainingGuide[] = [];
  isLoading = false;
  activeType = 'all';
  search = '';
  typeLabels = TYPE_LABELS;
  typeKeys = Object.keys(TYPE_LABELS);

  viewerOpen = false;
  viewerGuide: TrainingGuide | null = null;
  viewerEmbedUrl: SafeResourceUrl | null = null;
  viewerKind: 'text' | 'iframe' | 'video' | 'embed' | 'none' = 'none';

  constructor(
    private service: TrainingGuidesService,
    private sanitizer: DomSanitizer,
    private location: Location,
  ) {}

  goBack() {
    this.location.back();
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.service.getGuides().subscribe({
      next: (guides) => { this.guides = guides; this.isLoading = false; },
      error: () => { this.isLoading = false; },
    });
  }

  get filteredGuides(): TrainingGuide[] {
    const term = this.search.trim().toLowerCase();
    return this.guides.filter((g) => {
      const matchType = this.activeType === 'all' || g.guideType === this.activeType;
      const matchSearch = !term
        || g.title.toLowerCase().includes(term)
        || (g.shortDescription || '').toLowerCase().includes(term);
      return matchType && matchSearch;
    });
  }

  countFor(type: string): number {
    if (type === 'all') return this.guides.length;
    return this.guides.filter((g) => g.guideType === type).length;
  }

  openViewer(guide: TrainingGuide) {
    this.viewerGuide = guide;
    this.viewerEmbedUrl = null;
    this.viewerKind = 'none';

    let raw: string | null = null;
    if (guide.guideType === 'text') {
      this.viewerKind = 'text';
    } else if (guide.guideType === 'video') {
      const embed = this.toVideoEmbedUrl(guide.fileUrl);
      raw = embed || guide.fileUrl;
      this.viewerKind = embed ? 'iframe' : 'video';
    } else if (guide.guideType === 'pdf' && guide.sourceMode === 'link' && guide.fileUrl) {
      raw = `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(guide.fileUrl)}`;
      this.viewerKind = 'iframe';
    } else if (guide.guideType === 'pdf' && guide.fileUrl) {
      raw = guide.fileUrl;
      this.viewerKind = 'embed';
    } else if (guide.guideType === 'pptx' && guide.fileUrl) {
      raw = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(guide.fileUrl)}`;
      this.viewerKind = 'iframe';
    }
    if (raw) this.viewerEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(raw);

    this.viewerOpen = true;
  }

  closeViewer() {
    this.viewerOpen = false;
    this.viewerGuide = null;
    this.viewerEmbedUrl = null;
    this.viewerKind = 'none';
  }

  private toVideoEmbedUrl(url: string | null): string | null {
    if (!url) return null;
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
    return null;
  }
}
